/**
 * ***********************************************************
 * NOTE: PLEASE READ
 * ***********************************************************
 *
 * This relies on the Redux state being immutable. If any part of the state is mutated,
 * this will break and data updates will not propagate to components.
 *
 * When using these, you *must* pass Redux's `connect` a mapStateToProps generator: a function
 * that returns a mapStateToProps function. This is sometimes called "makeMapStateToProps".
 *
 * This ensures two things:
 *  - Each instance of a component has its own cache and its own data
 *  - The cache and memoization function will be properly destroyed when
 *    the component is unmounted, preventing memory leaks.
 *
 * https://github.com/reactjs/reselect#sharing-selectors-with-props-across-multiple-component-instances
 *
 * ***********************************************************
 *
 * As an example:
 *
 * const mapStateToProps = () => {
 *   const getPatientMedication = makeGetResourceSelector(CapsulePropTypes.patientMedication);
 *
 *   return (state, ownProps) => {
 *     const { id } = ownProps;
 *     const patientMedication = getPatientMedication(state, id);
 *
 *     return {
 *       patientMedication,
 *     };
 *   };
 * };
 */
import filter from 'lodash/filter';
import identity from 'lodash/identity';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import type { RootState } from 'src/app/state/store';

// a selector which uses deep equality for comparison rather than the basic === which is the default
// selector for reselect.
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

/**
 * creates a selector to get a specific resource from the state
 *
 * @param  {{ schemaName: {String} }} resourcePropType - The schema definition for the resource type
 *
 * @returns {function(state: {Object}, id: {String})} memoized selector
 *
 * @example Gets a DeliveryWindow with the ID 1000 from the state
 * const getDeliveryWindow = makeGetResourceSelector(CapsulePropTypes.deliveryWindow);
 * const deliveryWindow = getDeliveryWindow(state, '1000');
 */
export const makeGetResourceSelector = resourcePropType => {
  return createDeepEqualSelector(
    (state, id) => state.resources[resourcePropType.schemaName][id],
    resource => resource
  );
};

/**
 * creates a selector to get a collection of resources from the state
 *
 * @param {Object} capsulePropType - The prop type to find
 * @param {{ filter: {Object?}, orderBy: {Object?} }} options
 * @param {Function?} options.filter - The optional filter to apply
 * @param {Object?} options.orderBy - The optional orderBy clause to apply, defaulted to updated_at desc.
 *
 * @returns {function(state: Object)} memoized selector
 *
 * @example Gets all Patients from the state
 * const getPatients = makeGetCollectionSelector(CapsulePropTypes.patient, { filter, orderBy });
 * const allPatients = getPatients(state);
 */
export const makeGetCollectionSelector = (capsulePropType, options: any = {}) => {
  return createDeepEqualSelector(
    // re-filter only if any of the underlying resource data changes
    (state: RootState) => state.resources[capsulePropType.schemaName],
    resources => {
      const filterCallback = options?.filter ?? identity;
      const orderByCallback = options?.orderBy ?? [['attributes.updated_at'], ['desc']];

      return orderBy(filter(resources, filterCallback), orderByCallback);
    }
  );
};
